import { toast } from "react-toastify";
import Button from "../../../components/shared/Button/Button";
import { useUserData } from "../../../hooks/useUserData";
import { swingLibraryService } from "../../../services/swingLibrary";
import { allowedUsersToExport } from "../constants/allowedUsersToExport";
import { useAnalyzeVideos } from "../hooks/useAnalyzeVideos";
import {
  downloadJSONAsCSV,
  downloadJSONReport,
  downloadVideosHelper,
} from "../utils/download";
import { GeneratePdfButton } from "./PdfGeneration/GeneratePdfButton";
import { useIndicators } from "../hooks/useIndicators";
import { IoMdDownload } from "react-icons/io";
import { FiBarChart } from "react-icons/fi";
import { useSwingLibraryStore } from "../store/swingLibraryStore";
import { useMemo, useState } from "react";
import Popover from "../../../components/shared/Popover/Popover";
import { RiArrowDownSLine } from "react-icons/ri";
import Divider from "../../../components/shared/Divider/Divider";

const ActionButtons = () => {
  const { selectedVideos } = useSwingLibraryStore();

  const { userData } = useUserData();

  const analyzeVideosMutation = useAnalyzeVideos();
  const { data: indicators } = useIndicators();

  const [isOpenReports, setIsOpenReports] = useState(false);
  const [isOpenAdmin, setIsOpenAdmin] = useState(false);

  const selectedTableData = useMemo(() => {
    if (!selectedVideos) return [];
    return Object.values(selectedVideos);
  }, [selectedVideos]);

  const exportSelectedSwingsReportJSON = async () => {
    const tid = toast.loading("Exporting data...");
    const videoIds = selectedTableData.map((el) => el.id);
    const res = await swingLibraryService.preparePdfReport({
      gender: selectedTableData[0]?.userData?.gender
        ? selectedTableData[0]?.userData?.gender
        : "Male",
      golfClub: selectedTableData[0]?.userData?.golfClub
        ? selectedTableData[0]?.userData?.golfClub
        : "D",
      height: selectedTableData[0]?.userData?.height
        ? selectedTableData[0]?.userData?.height
        : "5′8″",
      videoIds,
    });
    downloadJSONReport(res);
    toast.dismiss(tid);
  };

  const exportSelectedSwings = async () => {
    const tid = toast.loading("Exporting data...");
    const videoIds = selectedTableData.map((el) => el.id);

    const indicatorUnits =
      indicators?.reduce((acc, category) => {
        category.items.forEach((item) => {
          acc[item.id] = { unit: item.unit ?? "" };
        });
        return acc;
      }, {}) ?? {};

    await downloadJSONAsCSV({
      filename: "swings",
      indicators:
        indicators?.flatMap((category) =>
          category.items.map((item) => ({ id: item.id, name: item.name }))
        ) ?? [],
      indicatorsUnits: indicatorUnits,
      userId: userData?.uid,
      videoIds,
    });
    toast.dismiss(tid);
  };

  const handleAnalyzeVideos = async () => {
    await analyzeVideosMutation.mutateAsync(selectedTableData);
  };

  return (
    <>
      <div className="flex gap-2">
        <span className="text-gray700">
          Swing Score Credits: {userData?.reportCredits || 0}
        </span>
        <span className="text-primary500 decoration-primary50 underline">
          <a href="/credits">Get credits</a>
        </span>
      </div>

      <Button
        color="gray100"
        isDisabled={selectedTableData.length === 0}
        onClick={() => downloadVideosHelper(selectedTableData)}
      >
        <IoMdDownload className="size-5" />
      </Button>

      {userData?.role === "admin" ||
      allowedUsersToExport.includes(userData?.uid || "") ? (
        <Button
          color="gray100"
          isDisabled={selectedTableData.length === 0}
          onClick={() => exportSelectedSwings()}
        >
          Export CSV
        </Button>
      ) : null}

      <Popover
        align="end"
        containerClassName="relative"
        contentClassName="bg-white rounded-lg shadow-lg border border-gray200 min-w-[240px]"
        isOpen={isOpenReports}
        onClose={() => setIsOpenReports(false)}
        side="bottom"
        trigger={
          <Button
            color="primary500"
            onClick={() => setIsOpenReports((v) => !v)}
            rounded="full"
            size="md"
            type="button"
          >
            Reports <RiArrowDownSLine className="size-5" />
          </Button>
        }
      >
        <div className="px-4 py-3 text-base font-semibold text-black">
          Reports
        </div>
        <Divider color="gray200" type="horizontal" />

        <div className="flex flex-col gap-1 px-1.5 py-1">
          <GeneratePdfButton />
        </div>
      </Popover>

      {userData?.role === "admin" && (
        <Popover
          align="end"
          containerClassName="relative"
          contentClassName="bg-white rounded-lg shadow-lg border border-gray200 min-w-[240px]"
          isOpen={isOpenAdmin}
          onClose={() => setIsOpenAdmin(false)}
          side="bottom"
          trigger={
            <Button
              color="white/primary700"
              onClick={() => setIsOpenAdmin((v) => !v)}
              rounded="full"
              size="md"
              type="button"
            >
              Admin <RiArrowDownSLine className="size-5" />
            </Button>
          }
        >
          <div className="px-4 py-3 text-base font-semibold text-black">
            Admin Actions
          </div>
          <Divider color="gray200" type="horizontal" />

          <div className="flex flex-col gap-1 px-1.5 py-1">
            <Button
              color="white/hover:shadow"
              rounded="md"
              className="font-normal w-full flex items-center justify-start"
              isDisabled={
                analyzeVideosMutation.isPending ||
                selectedTableData.length === 0
              }
              isLoading={analyzeVideosMutation.isPending}
              onClick={() => handleAnalyzeVideos()}
            >
              Analyze Videos
            </Button>

            <Button
              color="white/hover:shadow"
              rounded="md"
              className="font-normal w-full flex items-center justify-start"
              isDisabled={selectedTableData.length === 0}
              onClick={() => exportSelectedSwingsReportJSON()}
            >
              Export Report JSON
            </Button>
          </div>
        </Popover>
      )}
    </>
  );
};

export default ActionButtons;
